import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildHowToStepsSchema,
  buildSpeakableSchema,
} from "@/lib/schema";
import { CONSULAR_FAQ_CATEGORIES } from "@/content/consular-corpus/faq-defs";
import { CONSULAR_DOC_LANDINGS } from "@/content/consular-corpus/documents";
import { CONSULAR_COUNTRY_NAMES as CONSULAR_COUNTRY_LANDINGS } from "@/content/consular-corpus/names.gen";

const STEPS = [
  {
    title: "ตรวจต้นฉบับและหน่วยงานผู้ออกเอกสาร",
    body: "ส่งรูปเอกสารต้นฉบับให้ทีมตรวจว่าเป็นฉบับที่กรมการกงสุลรับรองได้ และต้องผ่านการรับรองต้นทาง (อำเภอ/เขต, กรมการปกครอง, สถาบันการศึกษา) ก่อนหรือไม่",
  },
  {
    title: "แปลรับรองตามรูปแบบที่ปลายทางกำหนด",
    body: "จัดทำคำแปลอังกฤษหรือภาษาปลายทางตามรูปแบบที่หน่วยงานผู้รับกำหนด พร้อมคำรับรองผู้แปลที่กรมการกงสุลใช้เป็นฐานในการประทับตรา",
  },
  {
    title: "ยื่นนิติกรณ์กรมการกงสุล",
    body: "ยื่นที่กองสัญชาติและนิติกรณ์ ถนนแจ้งวัฒนะ ค่าธรรมเนียม 200 บาท/ตราแบบปกติ 2–3 วันทำการ หรือ 400 บาท/ตราแบบด่วนรับวันเดียวเมื่อยื่นก่อน 11:30 น.",
  },
  {
    title: "รับรองสถานทูตปลายทางหรือใช้ Apostille",
    body: "ก่อน 28 กุมภาพันธ์ 2570 ยังต้องรับรองสถานทูตปลายทางในไทยอีกชั้น หลังอนุสัญญากรุงเฮกมีผลกับไทย เอกสารที่ส่งไปประเทศภาคีใช้ Apostille ใบเดียวแทนขั้นสถานทูตได้",
  },
  {
    title: "ส่งมอบและเก็บสำเนาไว้ใช้อ้างอิง",
    body: "ส่งเอกสารชุดสมบูรณ์คืนพร้อมสำเนาดิจิทัลทุกตราประทับ เพื่อใช้ยืนยันกับหน่วยงานปลายทางหากถูกขอเอกสารเพิ่ม",
  },
];

export const Route = createFileRoute("/consular-legalization/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/consular-corpus/faq");
    return { faqs: m.CONSULAR_FAQ_TH.slice(0, 12) };
  },
  component: ConsularHub,
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const faqs = loaderData?.faqs ?? [];
    return {
      ...pageHeadForProvince({
        title: "รับรองเอกสารกงสุล (นิติกรณ์ MFA) — ขั้นตอน ค่าธรรมเนียม ประเทศปลายทาง",
        description:
          "ศูนย์รวมบริการนิติกรณ์กรมการกงสุล: เลือกตามประเภทเอกสารหรือประเทศปลายทาง ดูลำดับการรับรองที่ถูกต้อง ระยะเวลา ค่าธรรมเนียมราชการ และเส้นทาง Apostille ที่จะมีผลกับไทยปี 2570",
        path: "/consular-legalization",
        alternates: [{ lang: "en", path: "/en/consular-legalization" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "รับรองเอกสารกงสุล", url: `${site}/consular-legalization` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildHowToStepsSchema({
              name: "ขั้นตอนรับรองเอกสารกงสุล (นิติกรณ์) สำหรับใช้ต่างประเทศ",
              description: "ลำดับงานจริงตั้งแต่ตรวจต้นฉบับถึงรับรองสถานทูตปลายทาง",
              path: "/consular-legalization",
              steps: STEPS.map((s, i) => ({ step: i + 1, title: s.title, body: s.body })),
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "นิติกรณ์กงสุลตามประเทศปลายทาง",
            itemListElement: CONSULAR_COUNTRY_LANDINGS.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/consular-legalization/country/${c.slug}`,
              name: c.nameTh,
            })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/consular-legalization")) },
      ],
    };
  },
});

function ConsularHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content" className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>รับรองเอกสารกงสุล</span>
        </nav>
        <h1 className="text-3xl font-extrabold md:text-4xl">
          รับรองเอกสารกงสุล (นิติกรณ์ กรมการกงสุล) — เลือกตามเอกสารหรือประเทศปลายทาง
        </h1>
        <p className="mt-4 max-w-3xl text-muted-foreground" data-speakable="true">
          นิติกรณ์คือการที่กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ รับรองลายมือชื่อผู้มีอำนาจและคำแปล
          เพื่อให้เอกสารไทยใช้ได้ในต่างประเทศ ค่าธรรมเนียมราชการ 200 บาทต่อตราประทับแบบปกติ (2–3 วันทำการ) และ 400 บาท
          แบบด่วนรับวันเดียวเมื่อยื่นก่อน 11:30 น. ก่อนวันที่ 28 กุมภาพันธ์ 2570 เอกสารส่วนใหญ่ยังต้องรับรองสถานทูตปลายทางอีกชั้น
        </p>

        <section className="mt-10">
          <h2 className="mb-4 text-xl font-bold">ขั้นตอนการทำงาน 5 ขั้น</h2>
          <ol className="space-y-3">
            {STEPS.map((s, i) => (
              <li key={s.title} id={`step-${i + 1}`} className="rounded-lg border bg-card p-4">
                <div className="font-semibold">{i + 1}. {s.title}</div>
                <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">หัวข้อความรู้และคำถามที่พบบ่อย</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {CONSULAR_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/consular-legalization/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleTh}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descTh}</p>
              </li>
            ))}
          </ul>
          <a href="/consular-legalization/faq" className="mt-4 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">
            ดูคำถาม-คำตอบทั้งหมด
          </a>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">นิติกรณ์ตามประเภทเอกสาร</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {CONSULAR_DOC_LANDINGS.map((d) => (
              <a key={d.slug} href={`/consular-legalization/document/${d.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {d.titleTh}
              </a>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">นิติกรณ์ตามประเทศปลายทาง</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {CONSULAR_COUNTRY_LANDINGS.map((c) => (
              <a key={c.slug} href={`/consular-legalization/country/${c.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {c.nameTh}
              </a>
            ))}
          </div>
        </section>

        <section className="mt-10 space-y-3">
          <h2 className="text-xl font-bold">คำถามที่พบบ่อย</h2>
          {faqs.map((f: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 4 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{f.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </section>

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ให้ทีมตรวจชุดเอกสารก่อนยื่น</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งประเทศปลายทาง หน่วยงานผู้รับ และกำหนดเวลาของคุณ — ตรวจให้ฟรีทางโทรศัพท์ LINE หรืออีเมล
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อทีมงาน</a>
        </section>
        <FaqSourceNote topic="consular" locale="th" />
      </main>
      <SiteFooter />
    </div>
  );
}